/*
 * The nodes every dialect of this editor shares, written out rather than extended from
 * prosemirror-schema-basic: the schema *is* the dialect, and a construct with no spec here simply
 * cannot be typed, pasted or dropped.
 *
 * Two rules shape the DOM sides. `toDOM` is the editor's own picture and mirrors what the reader of
 * the finished text sees, so a formatted paragraph looks the same before and after it is written.
 * `parseDOM` is the clipboard's door: it is what turns HTML from Word, Google Docs or a browser
 * page into this document.
 *
 * An image is never an `<img>`. An editor that fetched a remote picture would leak the fact that a
 * draft is being written to whoever owns the URL, so the node draws a chip carrying its alt text.
 *
 * What a dialect differs in — the heading's tag, what a list item may hold, which language a fence
 * may name, which destinations are allowed — is an option below. A dialect that needs a node this
 * file cannot describe writes that one spec itself and keeps the rest; nothing here knows which
 * dialect is being built.
 */

import type { DOMOutputSpec, NodeSpec } from "prosemirror-model"

export interface NodeOptions {
	/** The DOM tag a heading of this level is drawn as; a dialect owning its own `h1` says `h${level}`. */
	headingTag: (level: number) => string
	/** Whether an image's source may be kept; the refused ones keep their alt text and lose the node. */
	allowedHref: (dest: string) => boolean
	/**
	 * What the language on a pasted `<pre>` becomes in `params`, and `""` for one this dialect will
	 * not carry. The raw string is the `language-…` class or the `data-lang` attribute, as written.
	 */
	codeBlockParams: (raw: string) => string
	/** What a list item may hold: `paragraph block*` for a message, `block+` where a list nests freely. */
	listItemContent: string
	/** How a task item's box is drawn, beside the item's own content. */
	taskBox: (checked: boolean) => DOMOutputSpec
}

/**
 * A decorative box, not an input: where a task list is read-only everywhere else in the product,
 * the editor says the same thing rather than offering a tick that changes nothing.
 */
export function decorativeTaskBox(checked: boolean): DOMOutputSpec {
	return ["span", { class: "tb-editor-task", "aria-hidden": "true" }, checked ? "☑" : "☐"]
}

function attrString(dom: HTMLElement, name: string): string {
	return dom.getAttribute(name) ?? ""
}

/** The alignment of a table cell, from the attribute GFM writes or the style a rich editor writes. */
function cellAlign(dom: HTMLElement): string {
	const attr = (dom.getAttribute("align") ?? "").toLowerCase()
	if (attr === "left" || attr === "center" || attr === "right") return attr
	const style = dom.style.textAlign.toLowerCase()
	if (style === "left" || style === "center" || style === "right") return style
	return ""
}

export function baseNodes(options: NodeOptions) {
	return {
		doc: { content: "block+" },

		paragraph: {
			content: "inline*",
			group: "block",
			parseDOM: [{ tag: "p" }],
			toDOM: (): DOMOutputSpec => ["p", 0],
		},

		heading: {
			attrs: { level: { default: 1, validate: "number" } },
			content: "inline*",
			group: "block",
			defining: true,
			parseDOM: [1, 2, 3, 4, 5, 6].map((level) => ({
				tag: `h${level}`,
				getAttrs: (dom: HTMLElement) => ({
					level: Number(dom.getAttribute("data-level")) || level,
				}),
			})),
			toDOM: (node): DOMOutputSpec => [
				options.headingTag(node.attrs.level as number),
				{ "data-level": String(node.attrs.level) },
				0,
			],
		},

		blockquote: {
			content: "block+",
			group: "block",
			defining: true,
			parseDOM: [{ tag: "blockquote" }],
			toDOM: (): DOMOutputSpec => ["blockquote", 0],
		},

		code_block: {
			attrs: { params: { default: "", validate: "string" } },
			content: "text*",
			marks: "",
			group: "block",
			code: true,
			defining: true,
			parseDOM: [
				{
					tag: "pre",
					preserveWhitespace: "full",
					getAttrs: (dom: HTMLElement) => {
						const child = dom.firstElementChild
						const cls = child?.getAttribute("class") ?? dom.getAttribute("class") ?? ""
						const named = /(?:^|\s)language-([^\s]+)/.exec(cls)
						return {
							params: options.codeBlockParams(named?.[1] ?? dom.getAttribute("data-lang") ?? ""),
						}
					},
				},
			],
			toDOM: (node): DOMOutputSpec => [
				"pre",
				{ "data-lang": (node.attrs.params as string) || null },
				["code", 0],
			],
		},

		bullet_list: {
			attrs: { tight: { default: true, validate: "boolean" } },
			content: "list_item+",
			group: "block",
			// Foreign HTML carries no such attribute, and a pasted list is read as tight: only a list
			// this editor wrote itself can say otherwise.
			parseDOM: [
				{
					tag: "ul",
					getAttrs: (dom: HTMLElement) => ({ tight: dom.getAttribute("data-tight") !== "false" }),
				},
			],
			toDOM: (node): DOMOutputSpec => [
				"ul",
				{ "data-tight": node.attrs.tight ? "true" : "false" },
				0,
			],
		},

		ordered_list: {
			attrs: {
				order: { default: 1, validate: "number" },
				tight: { default: true, validate: "boolean" },
			},
			content: "list_item+",
			group: "block",
			parseDOM: [
				{
					tag: "ol",
					getAttrs: (dom: HTMLElement) => ({
						order: Number(dom.getAttribute("start")) || 1,
						tight: dom.getAttribute("data-tight") !== "false",
					}),
				},
			],
			toDOM: (node): DOMOutputSpec => [
				"ol",
				{
					start: node.attrs.order === 1 ? null : String(node.attrs.order),
					"data-tight": node.attrs.tight ? "true" : "false",
				},
				0,
			],
		},

		list_item: {
			attrs: { checked: { default: null, validate: "boolean|null" } },
			content: options.listItemContent,
			defining: true,
			parseDOM: [
				{
					tag: "li",
					getAttrs: (dom: HTMLElement) => {
						const box = dom.querySelector("input[type=checkbox]")
						if (box !== null) return { checked: box.hasAttribute("checked") }
						const marked = dom.getAttribute("data-checked")
						if (marked === "true" || marked === "false") return { checked: marked === "true" }
						return { checked: null }
					},
				},
			],
			toDOM: (node): DOMOutputSpec => {
				const checked = node.attrs.checked as boolean | null
				if (checked === null) return ["li", 0]
				return [
					"li",
					{ "data-checked": String(checked) },
					options.taskBox(checked),
					["span", { class: "tb-editor-task-body" }, 0],
				]
			},
		},

		table: {
			content: "table_row+",
			group: "block",
			isolating: true,
			parseDOM: [{ tag: "table" }],
			toDOM: (): DOMOutputSpec => ["table", ["tbody", 0]],
		},

		table_row: {
			content: "table_cell+",
			parseDOM: [{ tag: "tr" }],
			toDOM: (): DOMOutputSpec => ["tr", 0],
		},

		table_cell: {
			attrs: {
				header: { default: false, validate: "boolean" },
				align: { default: "", validate: "string" },
			},
			content: "inline*",
			isolating: true,
			parseDOM: [
				{ tag: "th", getAttrs: (dom: HTMLElement) => ({ header: true, align: cellAlign(dom) }) },
				{ tag: "td", getAttrs: (dom: HTMLElement) => ({ header: false, align: cellAlign(dom) }) },
			],
			toDOM: (node): DOMOutputSpec => [
				node.attrs.header ? "th" : "td",
				{ style: node.attrs.align === "" ? null : `text-align: ${node.attrs.align as string}` },
				0,
			],
		},

		horizontal_rule: {
			group: "block",
			parseDOM: [{ tag: "hr" }],
			toDOM: (): DOMOutputSpec => ["hr"],
		},

		image: {
			attrs: {
				src: { default: "", validate: "string" },
				alt: { default: "", validate: "string" },
				title: { default: "", validate: "string" },
			},
			inline: true,
			group: "inline",
			atom: true,
			draggable: true,
			parseDOM: [
				{
					tag: "img[src]",
					getAttrs: (dom: HTMLElement) => {
						const src = attrString(dom, "src")
						if (!options.allowedHref(src)) return false
						return { src, alt: attrString(dom, "alt"), title: attrString(dom, "title") }
					},
				},
			],
			toDOM: (node): DOMOutputSpec => [
				"span",
				{ class: "tb-editor-image", "data-src": node.attrs.src as string },
				`${(node.attrs.alt as string) || (node.attrs.src as string)}`,
			],
		},

		hard_break: {
			inline: true,
			group: "inline",
			selectable: false,
			leafText: () => "\n",
			parseDOM: [{ tag: "br" }],
			toDOM: (): DOMOutputSpec => ["br"],
		},

		text: { group: "inline" },
	} satisfies Record<string, NodeSpec>
}
